import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { teamService, type TeamDetailsResponse } from '@/services/teamService';
import { TeamMembersCard } from '@/components/team';
import { useAuth } from '@/providers/AuthProvider';
import { Skeleton } from '@/components/ui/skeleton';
import type { PageProps } from '@/types/page';
import type { TeamMemberRole } from '@arrowise/shared';
import { ArrowLeft, Settings, Crown, User, Shield } from 'lucide-react';

export const TeamDetailsPage: React.FC<PageProps> = ({ nav = true, footer = true }) => {
    const { id } = useParams<{ id: string }>();
    const { teams } = useAuth();
    const [teamDetails, setTeamDetails] = useState<TeamDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState<TeamMemberRole | null>(null);

    useEffect(() => {
        if (id) {
            loadTeamDetails();
        }
    }, [id]);

    const loadTeamDetails = async () => {
        if (!id) return;

        try {
            setLoading(true);
            const details = await teamService.getTeamDetailsById(id);
            setTeamDetails(details);
            setUserRole(details.userMembership.role);
        } catch (error: any) {
            console.error('Failed to load team details:', error);
            toast.error(error.message || 'Failed to load team details');
        } finally {
            setLoading(false);
        }
    };

    const getRoleIcon = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return <Crown className="size-4 text-yellow-600" />;
            case 'admin':
                return <Shield className="size-4 text-blue-600" />;
            default:
                return <User className="size-4 text-gray-600" />;
        }
    };

    const getRoleBadgeVariant = (role: TeamMemberRole) => {
        switch (role) {
            case 'owner':
                return 'default';
            case 'admin':
                return 'secondary';
            default:
                return 'outline';
        }
    };

    if (loading) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                    <div className="space-y-6">
                        <Skeleton className="h-8 w-48" />
                        <div className="grid gap-6">
                            <Skeleton className="h-48 rounded-xl" />
                            <Skeleton className="h-64 rounded-xl" />
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!teamDetails) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                    <div className="text-center py-12">
                        <h3 className="text-lg font-semibold mb-2">Team not found</h3>
                        <p className="text-muted-foreground mb-6">
                            The team you're looking for doesn't exist or you don't have access to it.
                        </p>
                        <Button asChild>
                            <Link to="/app/">
                                <ArrowLeft />
                                Back
                            </Link>
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const { team, members, userMembership } = teamDetails;
    const canManage = userRole === 'owner' || userRole === 'admin';
    const hasMultipleTeams = teams.length > 1;

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        {hasMultipleTeams && (
                            <Button variant="outline" size="sm" asChild>
                                <Link to="/app/teams">
                                    <ArrowLeft />
                                    Back
                                </Link>
                            </Button>
                        )}
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">{team.name}</h1>
                            <p className="text-muted-foreground">/{team.slug}</p>
                        </div>
                    </div>
                    {canManage && (
                        <Button asChild>
                            <Link to={`/app/teams/${team.id}/manage`}>
                                <Settings />
                                Manage Team
                            </Link>
                        </Button>
                    )}
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Team Information */}
                    <div className="lg:col-span-1">
                        <Card>
                            <CardHeader>
                                <CardTitle>Team Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <h4 className="font-medium mb-1">Description</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {team.description || 'No description provided'}
                                    </p>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Created</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {new Date(team.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Your Role</h4>
                                    <Badge variant={getRoleBadgeVariant(userMembership.role)} className="gap-1">
                                        {getRoleIcon(userMembership.role)}
                                        {userMembership.role}
                                    </Badge>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Joined</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {new Date(userMembership.joinedAt).toLocaleDateString()}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Team Members */}
                    <div className="lg:col-span-2">
                        <TeamMembersCard
                            members={members}
                            canManage={canManage}
                            canModify={false}
                            showInviteButton={false}
                            showJoinDate={false}
                            showRemoveButton={false}
                        />
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default TeamDetailsPage;
